import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiChevronLeft, FiChevronRight, FiDownload, FiClock, FiAlertCircle } from 'react-icons/fi';
import { getTeamMembers, getAuthToken } from '../services/api';
import { API_URL } from '../config';
import axios from 'axios';

// Helper to get axios config with auth token
const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// Get the Wednesday that starts the pay period containing the given date
const getPayPeriodStart = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 3 = Wednesday
  // Calculate days to subtract to get to Wednesday
  const daysToSubtract = day >= 3 ? day - 3 : day + 4;
  d.setDate(d.getDate() - daysToSubtract);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Get the Tuesday that ends the pay period
const getPayPeriodEnd = (startDate) => {
  const d = new Date(startDate);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Format date as YYYY-MM-DD
const formatDateISO = (date) => {
  return date.toISOString().split('T')[0];
};

// Format date for display
const formatDateDisplay = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Day names for the grid (Wed-Tue)
const DAY_ORDER = ['Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue'];

function Payroll() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [activeEntries, setActiveEntries] = useState([]);
  const [holidaysInPeriod, setHolidaysInPeriod] = useState([]);
  const [payPeriodStart, setPayPeriodStart] = useState(getPayPeriodStart(new Date()));
  const [loading, setLoading] = useState(true);

  const payPeriodEnd = getPayPeriodEnd(payPeriodStart);

  useEffect(() => {
    loadData();
  }, [payPeriodStart]);

  const loadData = async () => {
    setLoading(true);
    try {
      const config = await getAuthConfig();
      
      // Departed staff are included here on purpose. Their hours are still owed for the
      // week they worked, and payroll is the one place where leaving the company must
      // not make someone's time disappear. They are filtered back out below to the
      // periods they actually worked, so old employees don't pile up on every week.
      const teamResponse = await getTeamMembers(true);
      const members = teamResponse.data.team_members || teamResponse.data || [];
      setTeamMembers(members);

      // Load time entries for the pay period (daily entries only - no job_id)
      const entriesResponse = await axios.get(`${API_URL}/time-entries`, {
        params: {
          start_date: formatDateISO(payPeriodStart),
          end_date: formatDateISO(payPeriodEnd),
          type: 'payroll'
        },
        ...config
      });
      const entries = entriesResponse.data.time_entries || entriesResponse.data || [];
      setTimeEntries(entries);

      // Load active entries (currently clocked in)
      const activeResponse = await axios.get(`${API_URL}/time-entries/active`, config);
      const active = activeResponse.data.time_entries || activeResponse.data || [];
      // Filter to daily entries only
      setActiveEntries(active.filter(e => !e.job_id));

      // Load company holidays that fall inside this pay period
      try {
        const startIso = formatDateISO(payPeriodStart);
        const endIso = formatDateISO(payPeriodEnd);
        const hRes = await axios.get(`${API_URL}/holidays?year=${new Date(startIso).getFullYear()}`, config);
        const all = hRes.data || [];
        setHolidaysInPeriod(all.filter(h => h.date >= startIso && h.date <= endIso));
      } catch (e) {
        console.error('Holiday load error:', e);
        setHolidaysInPeriod([]);
      }

    } catch (error) {
      console.error('Error loading payroll data:', error);
    }
    setLoading(false);
  };

  // Navigate pay periods
  const goToPreviousWeek = () => {
    const newStart = new Date(payPeriodStart);
    newStart.setDate(newStart.getDate() - 7);
    setPayPeriodStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(payPeriodStart);
    newStart.setDate(newStart.getDate() + 7);
    setPayPeriodStart(newStart);
  };

  const goToCurrentWeek = () => {
    setPayPeriodStart(getPayPeriodStart(new Date()));
  };

  // Calculate hours for a specific team member on a specific date
  const getHoursForDay = (memberId, date) => {
    const dateStr = formatDateISO(date);

    const dayEntries = timeEntries.filter(entry => {
      if (entry.team_member_id !== memberId) return false;
      if (entry.job_id) return false; // Only daily entries
      const entryDate = entry.clock_in.split('T')[0];
      if (entryDate !== dateStr) return false;
      // Exclude unpaid time off from totals
      if (entry.work_type?.includes('Unpaid') && entry.work_type?.includes('Time Off')) return false;
      return true;
    });

    let totalMinutes = 0;

    for (const entry of dayEntries) {
      const clockIn = new Date(entry.clock_in + 'Z');
      const clockOut = entry.clock_out ? new Date(entry.clock_out + 'Z') : new Date();
      const minutes = (clockOut - clockIn) / (1000 * 60);

      // Subtract break time
      const breakMinutes = entry.total_break_minutes || 0;
      totalMinutes += Math.max(0, minutes - breakMinutes);
    }

    return totalMinutes / 60;
  };

  // Get only work hours (excluding time off) for a day
  const getWorkHoursForDay = (memberId, date) => {
    const dateStr = formatDateISO(date);
    const dayEntries = timeEntries.filter(entry => {
      if (entry.team_member_id !== memberId) return false;
      if (entry.job_id) return false;
      const entryDate = entry.clock_in.split('T')[0];
      return entryDate === dateStr && !entry.work_type?.includes('Time Off');
    });
    let totalMinutes = 0;
    for (const entry of dayEntries) {
      const clockIn = new Date(entry.clock_in + 'Z');
      const clockOut = entry.clock_out ? new Date(entry.clock_out + 'Z') : new Date();
      const minutes = (clockOut - clockIn) / (1000 * 60);
      const breakMinutes = entry.total_break_minutes || 0;
      totalMinutes += Math.max(0, minutes - breakMinutes);
    }
    return totalMinutes / 60;
  };

  // Get time-off hours for a day
  // Get PAID time-off hours for a day (excludes unpaid)
  const getTimeOffHoursForDay = (memberId, date) => {
    const dateStr = formatDateISO(date);
    const dayEntries = timeEntries.filter(entry => {
      if (entry.team_member_id !== memberId) return false;
      if (entry.job_id) return false;
      const entryDate = entry.clock_in.split('T')[0];
      if (entryDate !== dateStr) return false;
      // Only paid time off (not Unpaid)
      return entry.work_type?.includes('Time Off') && !entry.work_type?.includes('Unpaid');
    });
    let totalMinutes = 0;
    for (const entry of dayEntries) {
      const clockIn = new Date(entry.clock_in + 'Z');
      const clockOut = entry.clock_out ? new Date(entry.clock_out + 'Z') : new Date();
      const minutes = (clockOut - clockIn) / (1000 * 60);
      totalMinutes += Math.max(0, minutes);
    }
    return totalMinutes / 60;
  };

  // Get unpaid time-off hours for a day (for display only)
  const getUnpaidHoursForDay = (memberId, date) => {
    const dateStr = formatDateISO(date);
    const dayEntries = timeEntries.filter(entry => {
      if (entry.team_member_id !== memberId) return false;
      if (entry.job_id) return false;
      const entryDate = entry.clock_in.split('T')[0];
      return entryDate === dateStr && entry.work_type?.includes('Unpaid') && entry.work_type?.includes('Time Off');
    });
    let totalMinutes = 0;
    for (const entry of dayEntries) {
      const clockIn = new Date(entry.clock_in + 'Z');
      const clockOut = entry.clock_out ? new Date(entry.clock_out + 'Z') : new Date();
      const minutes = (clockOut - clockIn) / (1000 * 60);
      totalMinutes += Math.max(0, minutes);
    }
    return totalMinutes / 60;
  };

  // Get weekly time-off hours total
  const getWeeklyTimeOffHours = (memberId) => {
    const dates = getPayPeriodDates();
    return dates.reduce((total, date) => total + getTimeOffHoursForDay(memberId, date), 0);
  };

  // Check if a team member is currently clocked in
  const isClockedIn = (memberId) => {
    return activeEntries.some(e => e.team_member_id === memberId);
  };

  // Get dates for each day of the pay period
  const getPayPeriodDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(payPeriodStart);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  // Calculate weekly totals for a team member
  const getWeeklyTotal = (memberId) => {
    const dates = getPayPeriodDates();
    return dates.reduce((total, date) => total + getHoursForDay(memberId, date), 0);
  };

  // Holiday hours for an eligible team member in this pay period
  const getHolidayHours = (member) => {
    if (!member?.holiday_pay_eligible || holidaysInPeriod.length === 0) return 0;
    // Someone who has left doesn't accrue holiday pay — they weren't employed for it.
    // The server's payroll report applies the same rule, so the two agree.
    if (member.is_active === false) return 0;
    const daily = member.holiday_pay_hours || 10;
    return holidaysInPeriod.reduce((sum, h) => sum + daily * (h.is_half_day ? 0.5 : 1), 0);
  };

  // Who appears on this week's sheet. Active staff always; departed staff only for the
  // weeks they actually recorded time or took approved time off. That way a final week
  // still pays out, but past employees don't accumulate on every period afterwards.
  const visibleMembers = teamMembers.filter(member => {
    if (member.is_active !== false) return true;
    return getWeeklyTotal(member.id) > 0
        || getWeeklyTimeOffHours(member.id) > 0
        || isClockedIn(member.id);
  });

  // Calculate overtime (hours over 40 in a week)
  const getOvertime = (memberId) => {
    const total = getWeeklyTotal(memberId);
    return Math.max(0, total - 40);
  };

  // Calculate regular hours (up to 40)
  const getRegularHours = (memberId) => {
    const total = getWeeklyTotal(memberId);
    return Math.min(total, 40);
  };

  // Format hours for display
  const formatHours = (hours) => {
    if (hours === 0) return '-';
    return hours.toFixed(2);
  };

  // Get time off entries for a specific member on a specific date
  const getTimeOffForDay = (memberId, date) => {
    const dateStr = formatDateISO(date);

    const dayEntries = timeEntries.filter(entry => {
      if (entry.team_member_id !== memberId) return false;
      if (entry.job_id) return false;
      const entryDate = entry.clock_in.split('T')[0];
      return entryDate === dateStr && entry.work_type?.includes('Time Off');
    });

    if (dayEntries.length === 0) return null;

    // Return the type of time off
    const workType = dayEntries[0].work_type || '';
    if (workType.includes('Vacation')) return 'vacation';
    if (workType.includes('Sick')) return 'sick';
    if (workType.includes('Personal')) return 'personal';
    return 'timeoff';
  };

  // Get style for time off cell
  const getTimeOffCellStyle = (type) => {
    switch (type) {
      case 'vacation':
        return { backgroundColor: '#e3f2fd', color: '#1565c0' };
      case 'sick':
        return { backgroundColor: '#ffebee', color: '#c62828' };
      case 'personal':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
      default:
        return {};
    }
  };

  // Get label for time off type
  const getTimeOffLabel = (type) => {
    switch (type) {
      case 'vacation': return 'V';
      case 'sick': return 'S';
      case 'personal': return 'P';
      default: return '';
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const dates = getPayPeriodDates();
    
    const hasHolidays = holidaysInPeriod.length > 0;
    const holidayHeader = hasHolidays ? ',Holiday' : '';
    // Header row
    let csv = 'Employee,' + DAY_ORDER.map((day, i) => `${day} ${formatDateDisplay(dates[i])}`).join(',') + ',Regular,Overtime,PTO' + holidayHeader + ',Total\n';

    // Data rows
    for (const member of teamMembers) {
      const row = [member.name];

      for (const date of dates) {
        const hours = getHoursForDay(member.id, date);
        row.push(hours > 0 ? hours.toFixed(2) : '0');
      }

      row.push(getRegularHours(member.id).toFixed(2));
      row.push(getOvertime(member.id).toFixed(2));
      row.push(getWeeklyTimeOffHours(member.id).toFixed(2));
      const holHours = getHolidayHours(member);
      if (hasHolidays) row.push(holHours.toFixed(2));
      row.push((getWeeklyTotal(member.id) + holHours).toFixed(2));

      csv += row.join(',') + '\n';
    }

    // Optional: append a holidays-in-period note row at the bottom
    if (hasHolidays) {
      csv += '\nHolidays in this period:\n';
      for (const h of holidaysInPeriod) {
        csv += `${h.date},"${h.name}${h.is_half_day ? ' (half day)' : ''}"\n`;
      }
    }
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_${formatDateISO(payPeriodStart)}_to_${formatDateISO(payPeriodEnd)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const dates = getPayPeriodDates();
  const isCurrentWeek = formatDateISO(payPeriodStart) === formatDateISO(getPayPeriodStart(new Date()));

  return (
    <div className="payroll-page">
      <div className="page-header">
        <div>
          <h1><FiDollarSign /> Payroll</h1>
          <p className="subtitle">Weekly timesheet summary (Wed - Tue)</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={exportToCSV}
          disabled={loading}
        >
          <FiDownload /> Export CSV
        </button>
      </div>

      {/* Week Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        marginBottom: '25px',
        padding: '15px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <button
          onClick={goToPreviousWeek}
          style={{
            padding: '10px 15px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
        >
          <FiChevronLeft /> Previous
        </button>
        
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
            {formatDateDisplay(payPeriodStart)} - {formatDateDisplay(payPeriodEnd)}
          </div>
          <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
            Pay Period
          </div>
        </div>

        <button
          onClick={goToNextWeek}
          style={{
            padding: '10px 15px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
        >
          Next <FiChevronRight />
        </button>

        {!isCurrentWeek && (
          <button
            onClick={goToCurrentWeek}
            style={{
              padding: '10px 15px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #90caf9',
              borderRadius: '8px',
              cursor: 'pointer',
              color: '#1976d2',
              fontWeight: '500'
            }}
          >
            Current Week
          </button>
        )}
      </div>

      {/* Paid holidays in this pay period — heads-up so admins don't forget them */}
      {holidaysInPeriod.length > 0 && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px',
          padding: '14px 18px', marginBottom: '15px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '22px', lineHeight: 1 }}>🎉</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '4px' }}>
                Paid holiday{holidaysInPeriod.length > 1 ? 's' : ''} this pay period — remember to include holiday hours
              </div>
              {holidaysInPeriod.map(h => (
                <div key={h.id} style={{ fontSize: '13px', color: '#78350f', marginTop: '2px' }}>
                  • <strong>{new Date(h.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</strong> — {h.name}
                  {h.is_half_day && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 700, padding: '1px 6px', background: '#fef3c7', borderRadius: '4px' }}>HALF DAY</span>}
                </div>
              ))}
              <div style={{ fontSize: '12px', color: '#a16207', marginTop: '8px', fontStyle: 'italic' }}>
                Each row below shows holiday hours for eligible employees. Manage holidays at Holidays in the left nav.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        marginBottom: '15px',
        fontSize: '13px',
        color: '#666'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '12px', height: '12px', backgroundColor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: '2px' }}></span>
          Over 10 hours (daily overtime)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '12px', height: '12px', backgroundColor: '#c8e6c9', border: '1px solid #81c784', borderRadius: '2px' }}></span>
          Currently clocked in
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '12px', height: '12px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '2px' }}></span>
          <strong style={{ color: '#1565c0' }}>V</strong> Vacation
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '12px', height: '12px', backgroundColor: '#ffebee', border: '1px solid #ef9a9a', borderRadius: '2px' }}></span>
          <strong style={{ color: '#c62828' }}>S</strong> Sick
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '12px', height: '12px', backgroundColor: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: '2px' }}></span>
          <strong style={{ color: '#7b1fa2' }}>P</strong> Personal
        </span>
      </div>

      {/* Timesheet Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
          Loading payroll data...
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontWeight: '600',
                    borderBottom: '2px solid #e0e0e0',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#f8f9fa',
                    minWidth: '150px'
                  }}>
                    Employee
                  </th>
                  {dates.map((date, i) => (
                    <th key={i} style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      borderBottom: '2px solid #e0e0e0',
                      minWidth: '80px'
                    }}>
                      <div>{DAY_ORDER[i]}</div>
                      <div style={{ fontSize: '12px', fontWeight: '400', color: '#666' }}>
                        {formatDateDisplay(date)}
                      </div>
                    </th>
                  ))}
                  <th style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderBottom: '2px solid #e0e0e0',
                    backgroundColor: '#e8f5e9',
                    minWidth: '80px'
                  }}>
                    Regular
                  </th>
                  <th style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderBottom: '2px solid #e0e0e0',
                    backgroundColor: '#fff3e0',
                    minWidth: '80px'
                  }}>
                    OT
                  </th>
                  <th style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderBottom: '2px solid #e0e0e0',
                    backgroundColor: '#f3e5f5',
                    minWidth: '80px'
                  }}>
                    PTO
                  </th>
                  {holidaysInPeriod.length > 0 && (
                    <th style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#fffbeb',
                      minWidth: '80px',
                    }} title={`Holiday hours (${holidaysInPeriod.map(h => h.name).join(', ')}) for eligible employees`}>
                      Holiday
                    </th>
                  )}
                  <th style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderBottom: '2px solid #e0e0e0',
                    backgroundColor: '#e3f2fd',
                    minWidth: '80px'
                  }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleMembers.map((member, idx) => {
                  const isClocked = isClockedIn(member.id);
                  const weeklyTotal = getWeeklyTotal(member.id);
                  const regularHours = getRegularHours(member.id);
                  const overtime = getOvertime(member.id);
                  
                  return (
                    <tr 
                      key={member.id}
                      style={{
                        backgroundColor: isClocked ? '#e8f5e9' : (idx % 2 === 0 ? 'white' : '#fafafa')
                      }}
                    >
                      <td style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '500',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: isClocked ? '#e8f5e9' : (idx % 2 === 0 ? 'white' : '#fafafa')
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {member.name}
                          {isClocked && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              fontSize: '11px',
                              color: '#2e7d32',
                              backgroundColor: '#c8e6c9',
                              padding: '2px 6px',
                              borderRadius: '10px'
                            }}>
                              <FiClock size={10} /> Working
                            </span>
                          )}
                          {member.is_active === false && (
                            <span style={{
                              fontSize: '11px',
                              color: '#616161',
                              backgroundColor: '#e0e0e0',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontWeight: 600
                            }}>
                              Final pay — has left
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', fontWeight: '400' }}>
                          {member.role}
                        </div>
                      </td>
                      {dates.map((date, i) => {
                        const workHours = getWorkHoursForDay(member.id, date);
                        const ptoHours = getTimeOffHoursForDay(member.id, date);
                        const unpaidHours = getUnpaidHoursForDay(member.id, date);
                        const totalHours = workHours + ptoHours;
                        const isOvertime = totalHours > 10;
                        const timeOffType = getTimeOffForDay(member.id, date);
                        const timeOffStyle = timeOffType ? getTimeOffCellStyle(timeOffType) : {};
                        const timeOffLabel = timeOffType ? getTimeOffLabel(timeOffType) : '';

                        return (
                          <td key={i} style={{
                            padding: (ptoHours > 0 || unpaidHours > 0) && workHours > 0 ? '6px 8px' : '12px',
                            textAlign: 'center',
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: unpaidHours > 0 && workHours === 0 && ptoHours === 0 ? '#f5f5f5' : timeOffType && workHours === 0 ? timeOffStyle.backgroundColor : (isOvertime ? '#fff3e0' : undefined),
                            fontFamily: 'monospace'
                          }}>
                            {unpaidHours > 0 && workHours === 0 && ptoHours === 0 ? (
                              /* Full day unpaid - show but grayed out, not in totals */
                              <div style={{ color: '#9ca3af' }}>
                                <div style={{ fontSize: '10px', fontWeight: '600' }}>UNPAID</div>
                                <div style={{ textDecoration: 'line-through' }}>{formatHours(unpaidHours)}</div>
                              </div>
                            ) : ptoHours > 0 && workHours > 0 ? (
                              /* Split view: show both regular and PTO */
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '500' }}>
                                  {formatHours(workHours)}
                                </div>
                                <div style={{
                                  fontSize: '11px', fontWeight: '600',
                                  color: timeOffStyle.color || '#7b1fa2',
                                  backgroundColor: timeOffStyle.backgroundColor || '#f3e5f5',
                                  borderRadius: '4px', padding: '1px 4px', marginTop: '2px'
                                }}>
                                  {timeOffLabel} {formatHours(ptoHours)}
                                </div>
                              </div>
                            ) : ptoHours > 0 ? (
                              /* Full day paid time off */
                              <div style={{ color: timeOffStyle.color }}>
                                <div style={{ fontSize: '10px', fontWeight: '600' }}>{timeOffLabel}</div>
                                <div>{formatHours(ptoHours)}</div>
                              </div>
                            ) : (
                              /* Regular work day */
                              <span>
                                {isOvertime && (
                                  <FiAlertCircle size={12} style={{ color: '#f57c00', marginRight: '4px' }} />
                                )}
                                {formatHours(totalHours)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#e8f5e9',
                        fontWeight: '500',
                        fontFamily: 'monospace'
                      }}>
                        {formatHours(regularHours)}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: overtime > 0 ? '#fff3e0' : '#fafafa',
                        fontWeight: overtime > 0 ? '600' : '400',
                        fontFamily: 'monospace',
                        color: overtime > 0 ? '#e65100' : '#999'
                      }}>
                        {formatHours(overtime)}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: getWeeklyTimeOffHours(member.id) > 0 ? '#f3e5f5' : '#fafafa',
                        fontWeight: getWeeklyTimeOffHours(member.id) > 0 ? '600' : '400',
                        fontFamily: 'monospace',
                        color: getWeeklyTimeOffHours(member.id) > 0 ? '#7b1fa2' : '#999'
                      }}>
                        {formatHours(getWeeklyTimeOffHours(member.id))}
                      </td>
                      {holidaysInPeriod.length > 0 && (() => {
                        const holHours = getHolidayHours(member);
                        const eligible = member.holiday_pay_eligible;
                        return (
                          <td style={{
                            padding: '12px',
                            textAlign: 'center',
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: holHours > 0 ? '#fffbeb' : '#fafafa',
                            fontWeight: holHours > 0 ? '600' : '400',
                            fontFamily: 'monospace',
                            color: holHours > 0 ? '#92400e' : '#cbd5e1',
                          }} title={eligible ? `${holHours.toFixed(2)} hrs @ $${(member.hourly_rate || 0).toFixed(2)}/hr` : 'Not eligible for holiday pay (set in team profile)'}>
                            {eligible ? formatHours(holHours) : '–'}
                          </td>
                        );
                      })()}
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#e3f2fd',
                        fontWeight: '600',
                        fontFamily: 'monospace',
                        color: '#1565c0'
                      }}>
                        {formatHours(weeklyTotal + getHolidayHours(member))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <td style={{
                    padding: '14px 16px',
                    fontWeight: '600',
                    borderTop: '2px solid #e0e0e0',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#f5f5f5'
                  }}>
                    Daily Totals
                  </td>
                  {dates.map((date, i) => {
                    const dayTotal = visibleMembers.reduce((sum, m) => sum + getHoursForDay(m.id, date), 0);
                    return (
                      <td key={i} style={{
                        padding: '14px 12px',
                        textAlign: 'center',
                        fontWeight: '600',
                        borderTop: '2px solid #e0e0e0',
                        fontFamily: 'monospace'
                      }}>
                        {formatHours(dayTotal)}
                      </td>
                    );
                  })}
                  <td style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#c8e6c9',
                    fontFamily: 'monospace'
                  }}>
                    {formatHours(visibleMembers.reduce((sum, m) => sum + getRegularHours(m.id), 0))}
                  </td>
                  <td style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#ffe0b2',
                    fontFamily: 'monospace'
                  }}>
                    {formatHours(visibleMembers.reduce((sum, m) => sum + getOvertime(m.id), 0))}
                  </td>
                  <td style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#e1bee7',
                    fontFamily: 'monospace',
                    color: '#7b1fa2'
                  }}>
                    {formatHours(visibleMembers.reduce((sum, m) => sum + getWeeklyTimeOffHours(m.id), 0))}
                  </td>
                  {holidaysInPeriod.length > 0 && (
                    <td style={{
                      padding: '14px 12px',
                      textAlign: 'center',
                      fontWeight: '600',
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fef3c7',
                      fontFamily: 'monospace',
                      color: '#92400e',
                    }}>
                      {formatHours(visibleMembers.reduce((sum, m) => sum + getHolidayHours(m), 0))}
                    </td>
                  )}
                  <td style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#bbdefb',
                    fontFamily: 'monospace',
                    color: '#1565c0'
                  }}>
                    {formatHours(visibleMembers.reduce((sum, m) => sum + getWeeklyTotal(m.id) + getHolidayHours(m), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {!loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Total Regular Hours</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#2e7d32' }}>
              {visibleMembers.reduce((sum, m) => sum + getRegularHours(m.id), 0).toFixed(1)}
            </div>
          </div>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Total Overtime</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#e65100' }}>
              {visibleMembers.reduce((sum, m) => sum + getOvertime(m.id), 0).toFixed(1)}
            </div>
          </div>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Employees Working Now</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1565c0' }}>
              {activeEntries.length}
            </div>
          </div>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Total Hours This Period</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#333' }}>
              {visibleMembers.reduce((sum, m) => sum + getWeeklyTotal(m.id), 0).toFixed(1)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payroll;